import React, { useCallback } from 'react';
import { useTranslation } from 'react-i18next';

import { useTheme } from '../../../contexts/ThemeContext';

// Switch geometry — was `--switch-w/--switch-h/--thumb-size` in the original
// CSS. The thumb's "on" translation is `width - thumb - 4` = 40 - 18 - 4 =
// 18 px, baked into the `theme-switch--on` extra class below.
//
// Tailwind owns layout / colors / transitions; the inner thumb uses
// `dark:translate-x-...` so the toggle position follows the data-theme
// attribute that ThemeContext sets on <html>.

const SWITCH_BTN_CLASS =
  'inline-flex items-center border-0 bg-transparent cursor-pointer p-0';

const SWITCH_TRACK_CLASS =
  'relative inline-flex items-center justify-between w-10 h-[22px] px-1 rounded-full' +
  ' bg-[var(--color-border-strong)] transition-[background-color] duration-base ease-linear' +
  ' dark:bg-[var(--color-accent)]';

const SWITCH_THUMB_CLASS =
  'absolute top-[2px] left-[2px] w-[18px] h-[18px] rounded-full' +
  ' bg-[var(--color-bg-primary)] [box-shadow:0_1px_2px_rgba(0,0,0,0.2)]' +
  ' transition-transform duration-base ease-linear' +
  ' dark:translate-x-[18px]';

const ThemeMode: React.FC = () => {
  const { theme, toggleTheme } = useTheme();
  const { t } = useTranslation();
  const isDark = theme === 'dark';

  const handleClick = useCallback((): void => {
    toggleTheme();
  }, [toggleTheme]);

  return (
    <button
      type="button"
      role="switch"
      aria-checked={isDark}
      aria-label={t('header.theme.toggle')}
      className={SWITCH_BTN_CLASS}
      onClick={handleClick}
    >
      <span className={SWITCH_TRACK_CLASS}>
        <span className={SWITCH_THUMB_CLASS} aria-hidden="true" />
      </span>
    </button>
  );
};

export default ThemeMode;
